// @flow
import * as React from 'react';

import clsx from 'clsx';
import { connect } from 'react-redux';

import { removeCartProduct } from '@gandi/cart';
import type { NotifyAction } from '@gandi/notifications';

import type { TranslatorProps } from '@gandi/translate';
import {
  Base,
  Badge,
  Cell,
  IconButton,
  TooltipContainer,
  Tooltip,
} from '@gandi/ui';
import { ListItem, ListLoader, ListRow } from '@gandi/ui/deprecated';

import { getExpireDataInfo } from 'src/DomainRenew/componentsNDS/modules/helpers';

import type {
  ExtendedRenewableProduct,
  QuotedProduct,
} from 'src/DomainRenew/componentsNDS/types';

import CellRowGroup from '../../../DomainTransfer/components/DomainList/CellRowGroup';
import CellRowToCol from '../../../DomainTransfer/components/DomainList/CellRowToCol';

import PriceWithOptionsCell from './PriceWithOptionsCell';

import styles from './RenewableProductRow.scss';

type OwnProps = {
  addRenewableProductsToCart: (
    translator: TranslatorProps,
    products: Array<ExtendedRenewableProduct>,
  ) => Promise<any>,
  disabled?: boolean,
  entry: ExtendedRenewableProduct,
  isAddingToCart?: boolean,
  loadingQuotations?: boolean,
  quotedProduct: QuotedProduct,
  translator: TranslatorProps,
};

type DispatchProps = {
  notifyDanger: NotifyAction,
  removeCartProduct: any,
};

type Props = { ...DispatchProps, ...OwnProps };

type State = {
  loading: boolean,
  ...
};

class RenewableProductRow extends React.PureComponent<Props, State> {
  state: State = {
    loading: false,
  };

  handleItemClick: (evt: SyntheticMouseEvent<>) => Promise<void> = async (
    evt: SyntheticMouseEvent<>,
  ) => {
    evt.preventDefault();

    const {
      translator: { __ },
      entry,
    } = this.props;
    const { product } = entry;

    const productId = product && product.id;

    if (productId) {
      try {
        await this.props.removeCartProduct(productId);
      } catch (err) {
        this.props.notifyDanger(
          __('Unable to remove the product in the cart.'),
        );
      }
    } else {
      this.setState({ loading: true });

      try {
        await this.props.addRenewableProductsToCart(this.props.translator, [
          entry,
        ]);
      } catch (err) {
        this.props.notifyDanger(__('Unable to add the products in the cart.'));
      }
    }

    this.setState({ loading: false });
  };

  renderExpirationDate(): null | React.Node {
    const { entry, translator } = this.props;
    const { expireDate, expireTheme } = getExpireDataInfo(
      entry.meta_data.expires_at,
      translator,
    );

    if (!expireDate || !expireTheme) return null;

    return (
      <TooltipContainer
        id={`expire-date-${entry.id}`}
        renderReference={getReferenceProps => (
          // $FlowFixMe[cannot-spread-inexact]
          <Badge intent={expireTheme} {...getReferenceProps()}>
            {expireDate}
          </Badge>
        )}
      >
        {getTooltipProps => (
          <Tooltip {...getTooltipProps()}>
            {translator.formatDate(entry.meta_data.expires_at, 'LLLL')}
          </Tooltip>
        )}
      </TooltipContainer>
    );
  }

  renderPriceCell(): React.Node {
    const { translator, quotedProduct } = this.props;

    return (
      <PriceWithOptionsCell
        translator={translator}
        quotedProduct={quotedProduct}
      />
    );
  }

  renderMainCell(): React.Node {
    const { entry } = this.props;
    return (
      <Cell flexGrow1>
        <CellRowToCol
          className={clsx(styles.marginTopOnMobile, styles.renewables)}
        >
          <Base tagName="span" marginRight="xsmall">
            {entry.name}
          </Base>{' '}
          <Base tagName="span">{this.renderExpirationDate()}</Base>
        </CellRowToCol>
      </Cell>
    );
  }

  getIconButtonKind({
    loading,
    addedToCart,
  }: {
    loading: boolean,
    addedToCart: boolean,
  }): 'ghost' | 'outline' | void {
    if (loading) return 'ghost';
    if (addedToCart) return undefined;
    return 'outline';
  }

  renderSpinnerCell(): React.Node {
    return <ListLoader />;
  }

  render(): React.Node {
    const {
      entry,
      isAddingToCart,
      translator: { __ },
    } = this.props;

    const { loading } = this.state;

    const product = entry.product;

    const addedToCart = !!product;
    const actionIcon = addedToCart ? 'checkmark' : 'cart';
    const actionLoading =
      loading || (isAddingToCart && !addedToCart) || !!product?.isWorking;

    return (
      <ListRow>
        <ListItem className={styles.productRow} backgroundColor="white">
          <CellRowGroup>
            {this.renderMainCell()}
            {this.props.quotedProduct && this.renderPriceCell()}
            {this.props.loadingQuotations && this.renderSpinnerCell()}
          </CellRowGroup>
        </ListItem>
        <IconButton
          marginBottom="xsmall"
          marginTop="xsmall"
          marginRight="xsmall"
          icon={actionIcon}
          intent={addedToCart && !actionLoading ? 'success' : undefined}
          kind={this.getIconButtonKind({ loading: actionLoading, addedToCart })}
          loading={actionLoading}
          onClick={this.handleItemClick}
          disabled={this.props.disabled}
        >
          {addedToCart ? __('Remove from cart') : __('Add to cart')}
        </IconButton>
      </ListRow>
    );
  }
}

const mapDispatchToProps = {
  removeCartProduct,
};

// $FlowFixMe[signature-verification-failure]
// $FlowFixMe[underconstrained-implicit-instantiation]
export default connect(null, mapDispatchToProps)(RenewableProductRow);
